import { useEffect, useState } from "react";
import api from "./api";
import Notification from "./Notification"; // ✅ Added

const DAYS = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const emptyDay = (weekday) => ({
  weekday,
  isClosed: false,
  intervals: [{ open: "11:00", close: "22:00" }],
});

const timeOk = (s) => /^\d{2}:\d{2}$/.test(String(s || ""));

export default function OpeningHours() {
  const [rows, setRows] = useState(DAYS.map((d) => emptyDay(d.value)));
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [note, setNote] = useState({ message: "", type: "" }); // ✅ Added

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/api/opening-hours");
      const byDay = Object.fromEntries((data || []).map((r) => [Number(r.weekday), r]));
      setRows(
        DAYS.map((d) => {
          const r = byDay[d.value];
          if (!r) return emptyDay(d.value);
          return {
            weekday: d.value,
            isClosed: !!r.isClosed,
            intervals:
              Array.isArray(r.intervals) && r.intervals.length
                ? r.intervals.map((x) => ({ open: x.open, close: x.close }))
                : [{ open: "11:00", close: "22:00" }],
          };
        })
      );
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to load opening hours");
      setNote({ message: "Failed to load opening hours", type: "error" }); // ✅ Added
    } finally {
      setLoading(false);
    }
  }

  function updateDay(weekday, patch) {
    setRows((prev) =>
      prev.map((row) => (row.weekday === weekday ? { ...row, ...patch } : row))
    );
  }

  function updateInterval(weekday, idx, patch) {
    setRows((prev) =>
      prev.map((row) => {
        if (row.weekday !== weekday) return row;
        const list = [...row.intervals];
        list[idx] = { ...list[idx], ...patch };
        return { ...row, intervals: list };
      })
    );
  }

  function addInterval(weekday) {
    setRows((prev) =>
      prev.map((row) =>
        row.weekday === weekday
          ? { ...row, intervals: [...row.intervals, { open: "18:00", close: "22:00" }] }
          : row
      )
    );
  }

  function removeInterval(weekday, idx) {
    setRows((prev) =>
      prev.map((row) => {
        if (row.weekday !== weekday) return row;
        const list = row.intervals.filter((_, i) => i !== idx);
        return { ...row, intervals: list.length ? list : [{ open: "11:00", close: "22:00" }] };
      })
    );
  }

  async function saveDay(day) {
    setErr("");
    try {
      if (!day.isClosed) {
        const bad = day.intervals.some(
          (iv) => !timeOk(iv.open) || !timeOk(iv.close) || iv.open >= iv.close
        );
        if (bad) return setErr("Please fix times (format HH:MM, open < close).");
      }
      await api.post("/api/opening-hours", {
        weekday: day.weekday,
        isClosed: day.isClosed,
        intervals: day.isClosed ? [] : day.intervals,
      });
      setNote({ message: "Saved successfully ✅", type: "success" }); // ✅ Added
      await load();
    } catch (e) {
      setErr(e?.response?.data?.error || "Save failed");
      setNote({ message: "Save failed ❌", type: "error" }); // ✅ Added
    }
  }

  async function clearDay(day) {
    if (!window.confirm("Clear this weekday?")) return;
    setErr("");
    try {
      await api.delete(`/api/opening-hours/${day.weekday}`);
      await load();
      setNote({ message: "Cleared successfully 🗑️", type: "success" }); // ✅ Added
    } catch (e) {
      setErr(e?.response?.data?.error || "Delete failed");
      setNote({ message: "Delete failed", type: "error" }); // ✅ Added
    }
  }

  return (
    <div style={container}>
      <h2 style={title}>Opening Hours</h2>
      {err && <div style={msgError}>{err}</div>}

      <div style={{ overflowX: "auto" }}>
        <table style={table}>
          <thead>
            <tr style={{ background: "#f8f4e4" }}>
              <th style={th}>Day</th>
              <th style={th}>Closed</th>
              <th style={th}>Open - Close Intervals</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="4" style={tdCenter}>Loading…</td></tr>
            ) : (
              rows.map((row) => {
                const label = DAYS.find((d) => d.value === row.weekday)?.label ?? row.weekday;
                return (
                  <tr key={row.weekday}>
                    <td style={tdDay}>{label}</td>
                    <td style={td}>
                      <input
                        type="checkbox"
                        checked={row.isClosed}
                        onChange={(e) => updateDay(row.weekday, { isClosed: e.target.checked })}
                      />
                    </td>
                    <td style={td}>
                      {row.isClosed ? (
                        <span style={{ color: "#777" }}>Closed</span>
                      ) : (
                        <div style={{ display: "grid", gap: 8 }}>
                          {row.intervals.map((iv, idx) => (
                            <div key={idx} style={intervalRow}>
                              <input
                                type="time"
                                value={iv.open}
                                onChange={(e) =>
                                  updateInterval(row.weekday, idx, { open: e.target.value })
                                }
                                style={input}
                              />
                              <span>–</span>
                              <input
                                type="time"
                                value={iv.close}
                                onChange={(e) =>
                                  updateInterval(row.weekday, idx, { close: e.target.value })
                                }
                                style={input}
                              />
                              {row.intervals.length > 1 && (
                                <button
                                  onClick={() => removeInterval(row.weekday, idx)}
                                  style={btnSmallDanger}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => addInterval(row.weekday)}
                            style={btnSmall}
                            type="button"
                          >
                            + Add Interval
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button onClick={() => saveDay(row)} style={btnPrimary}>Save</button>
                        <button onClick={() => clearDay(row)} style={btnGhost}>Clear</button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p style={tipText}>
        💡 Add multiple intervals for split shifts (e.g. 12:00–15:00 & 18:00–22:00). Toggle “Closed” for days off.
      </p>

      {/* ✅ Notification */}
      <Notification
        message={note.message}
        type={note.type}
        onClose={() => setNote({ message: "", type: "" })}
      />
    </div>
  );
}

/* ================== STYLES ================== */
const container = { display: "grid", gap: 18, color: "#333" };
const title = { fontSize: 22, fontWeight: 700, textAlign: "center", color: "#d4a017" };
const table = { width: "100%", borderCollapse: "collapse", minWidth: 800, background: "#fff" };
const th = {
  textAlign: "left",
  padding: "10px 8px",
  borderBottom: "2px solid #e8d58a",
  fontWeight: 700,
  fontSize: 14,
  color: "#b8860b",
  whiteSpace: "nowrap",
};
const td = { padding: "10px 8px", borderBottom: "1px solid #f3f3f3", verticalAlign: "top" };
const tdDay = { ...td, fontWeight: 600, color: "#444" };
const tdCenter = { ...td, textAlign: "center", color: "#666" };
const intervalRow = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const input = { padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13 };
const btnPrimary = {
  padding: "6px 12px",
  borderRadius: 6,
  background: "#d4a017",
  color: "#fff",
  border: "none",
  fontWeight: 600,
  cursor: "pointer",
};
const btnGhost = {
  padding: "6px 12px",
  borderRadius: 6,
  background: "#fff8e1",
  color: "#333",
  border: "1px solid #e0c97a",
  cursor: "pointer",
};
const btnSmall = {
  padding: "4px 8px",
  borderRadius: 6,
  background: "#fff",
  color: "#333",
  border: "1px solid #ddd",
  cursor: "pointer",
  fontSize: 12,
};
const btnSmallDanger = {
  padding: "3px 6px",
  borderRadius: 6,
  background: "#c62828",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontSize: 12,
};
const msgError = { color: "#b00020", fontSize: 13, textAlign: "center" };
const tipText = {
  fontSize: 12,
  color: "#666",
  textAlign: "center",
  marginTop: 10,
  background: "#fffef6",
  border: "1px solid #f0e4b1",
  borderRadius: 8,
  padding: "8px 10px",
};
